/**
 * LocationProvider - Context provider for location services
 * 
 * Wraps the app to provide location tracking across all components.
 * Auto-starts tracking when user grants permission.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from './useLocation';
import { toast } from 'sonner';

const LocationContext = createContext(null);

export function useLocationContext() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocationContext must be used within LocationProvider');
  }
  return context;
}

export function LocationProvider({ children }) {
  const locationHook = useLocation();
  const [hasPromptedPermission, setHasPromptedPermission] = useState(false);
  const [shouldAutoTrack, setShouldAutoTrack] = useState(() => {
    // Check if user has previously enabled location
    return localStorage.getItem('ps_location_enabled') === 'true';
  });

  // Auto-start tracking if user previously granted permission
  useEffect(() => {
    if (shouldAutoTrack && locationHook.permissionStatus === 'granted' && !locationHook.isTracking) {
      locationHook.startTracking();
    }
  }, [shouldAutoTrack, locationHook.permissionStatus, locationHook.isTracking, locationHook]);

  // Enable location tracking (called from UI)
  const enableLocationTracking = async () => {
    const success = await locationHook.requestPermission();
    if (success) {
      localStorage.setItem('ps_location_enabled', 'true');
      setShouldAutoTrack(true);
      toast.success('📍 Location enabled! Distance to other golfers will now be accurate.');
      return true;
    }
    return false;
  };

  // Disable location tracking
  const disableLocationTracking = () => {
    locationHook.stopTracking();
    localStorage.removeItem('ps_location_enabled');
    setShouldAutoTrack(false);
    toast.success('Location tracking disabled.');
  };

  // Prompt user for location permission (show once)
  const promptForLocation = () => {
    if (hasPromptedPermission) return;
    setHasPromptedPermission(true);
    
    // Don't prompt if already granted or denied
    if (locationHook.permissionStatus !== 'prompt') return;
    
    return enableLocationTracking();
  };

  const value = {
    ...locationHook,
    enableLocationTracking,
    disableLocationTracking,
    promptForLocation,
    shouldAutoTrack
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export default LocationProvider;
